import { ApolloClient, ApolloLink } from '@apollo/client';
import { baseClientOption } from 'apollo/client';
import { createAuthenticationLink, errorLink, mainLink } from 'apollo/link';
import { AtmnhaAuthenticationModel } from 'components/providers/authentication-provider';
import ListOldProvinceProvider from 'components/providers/list-old-provinces-provider';
import ListProvinceProvider from 'components/providers/list-province-provider';
import RealEstateConfigurationProvider from 'components/providers/real-estate-configuration-provider';
import Splash from 'components/shared/splash';
import {
  DEFAULT_DESCRIPTION,
  DEFAULT_OG_IMAGE,
  DEFAULT_TITLE,
  MAIN_DOMAIN,
  SITE_MAP,
} from 'globalConstants';
import { B2CUser, USER, UserData } from 'graphql/main/queries';
import { isEqual } from 'lodash';
import type { NextPage } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import queryString from 'query-string';
import { Fragment, useEffect } from 'react';
import { detectDeviceByUserAgent, parseCookiesStringToObject } from 'utils';

const Valuation = dynamic(() => import('components/desktop/valuation'), {
  ssr: false,
  loading: () => <Splash />,
});
const ValuationMobile = dynamic(() => import('components/mobile/valuation'), {
  ssr: false,
  loading: () => <Splash />,
});

interface Props {
  device?: 'mobile' | 'desktop';
  currentUser?: B2CUser | null;
}

const ValuationPage: NextPage = ({ device, currentUser }: Props) => {
  const title = `Định giá | ${DEFAULT_TITLE}`;
  const description = DEFAULT_DESCRIPTION;
  const ogUrl = `${MAIN_DOMAIN}/${SITE_MAP.VALUATION.INDEX}`;
  const ogImage = DEFAULT_OG_IMAGE;
  const router = useRouter();
  const { asPath, push } = router;

  useEffect(() => {
    if (!currentUser) {
      push({
        pathname: `/${SITE_MAP.SIGN_IN.INDEX}`,
        query: queryString.stringify({ redirect: asPath }, { encode: false }),
      });
    }
  }, []);

  return currentUser ? (
    <Fragment>
      <Head>
        <meta
          name='viewport'
          content='width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover'
        />
        <meta name='description' content={description} />
        <meta name='og:title' content={title} />
        <meta name='og:description' content={description} />
        <meta name='og:url' content={ogUrl} />
        <meta name='og:image' content={ogImage} />
        <title>{title}</title>
      </Head>
      <RealEstateConfigurationProvider>
        <ListOldProvinceProvider>
          <ListProvinceProvider>
            {isEqual(device, 'mobile') ? (
              <ValuationMobile />
            ) : isEqual(device, 'desktop') ? (
              <Valuation />
            ) : (
              <Splash />
            )}
          </ListProvinceProvider>
        </ListOldProvinceProvider>
      </RealEstateConfigurationProvider>
    </Fragment>
  ) : null;
};

ValuationPage.getInitialProps = async (context) => {
  const { req } = context;
  const userAgent = req ? req.headers['user-agent'] : navigator.userAgent;
  const cookiesString = req ? req.headers.cookie : document.cookie;
  const device = detectDeviceByUserAgent(userAgent);
  let currentUser;
  const cookiesObject = parseCookiesStringToObject(cookiesString);
  const atmnhaAuthenticationString = (cookiesObject as any)['atmnha_authentication_cookie'] || '{}';
  const atmnhaAuthentication = JSON.parse(atmnhaAuthenticationString) as AtmnhaAuthenticationModel;
  const { accessToken } = atmnhaAuthentication ?? {};
  if (accessToken) {
    const authLink = createAuthenticationLink(accessToken);
    const mainClient = new ApolloClient({
      ...baseClientOption,
      link: ApolloLink.from([errorLink, authLink, mainLink]),
    });
    const { data: userData, error: userError } = await mainClient.query<UserData>({
      query: USER,
    });
    if (!userError) {
      currentUser = userData.user;
    }
  }

  return {
    device,
    currentUser,
  };
};

export default ValuationPage;
